import React, { useState, useEffect, useContext } from 'react';
import { Grid, Typography, Button, CircularProgress } from '@material-ui/core';
import { Save as SaveIcon, HighlightOff as HighlightOffIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';

import { SectionWrapper, ToggleTickSwitch } from 'components';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { SSO_SERVICES } from 'Services';
import Utils from 'Shared/Utils';

import { SSO_PROVIDER } from './constants';

const AUTH_METHOD_OPTIONS = [
  { value: 'password', label: 'Password' },
  { value: SSO_PROVIDER.MICROSOFT, label: 'Microsoft' },
  { value: SSO_PROVIDER.GOOGLE, label: 'Google' },
];

const AuthMethodsSection = ({ level, levelId }) => {
  const { dispatch } = useContext(Context);

  const [methods, setMethods] = useState([]);
  const [initialMethods, setInitialMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAuthMethods();
  }, [level, levelId]);

  const fetchAuthMethods = async () => {
    setLoading(true);
    try {
      const response = await SSO_SERVICES.getAuthMethods(level, levelId);
      if (Utils.checkIfSuccess(response)) {
        const effective = response?.data?.effective_methods || [];
        setMethods(effective);
        setInitialMethods(effective);
      }
    } catch (error) {
      console.error('Failed to load auth methods:', error);
    }
    setLoading(false);
  };

  const handleToggle = (value) => {
    setMethods((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );
  };

  const hasChanges =
    JSON.stringify([...methods].sort()) !== JSON.stringify([...initialMethods].sort());

  const handleSave = async () => {
    if (methods.length === 0) {
      dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          notification: {
            severity: 'error',
            message: 'At least one authentication method must be enabled',
          },
        },
      });
      return;
    }

    setSaving(true);
    try {
      const response = await SSO_SERVICES.updateAuthMethods(level, levelId, methods);
      if (Utils.checkIfSuccess(response)) {
        setInitialMethods([...methods]);
        dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            notification: {
              severity: 'success',
              message: 'Authentication methods updated successfully',
            },
          },
        });
      } else {
        dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            notification: {
              severity: 'error',
              message:
                response?.data?.message ||
                response?.message ||
                'Failed to update authentication methods',
            },
          },
        });
      }
    } catch (error) {
      console.error('Failed to update auth methods:', error);
      dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          notification: {
            severity: 'error',
            message: 'Failed to update authentication methods',
          },
        },
      });
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setMethods([...initialMethods]);
  };

  return (
    <div style={{ marginBottom: 24 }}>
    <SectionWrapper
      title="Allowed Authentication Methods"
      rightSection={
        hasChanges && (
          <>
            <Button
              size="small"
              onClick={handleSave}
              disabled={saving || methods.length === 0}
              data-testid="save-auth-methods"
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SaveIcon color="secondary" />
                )
              }
            >
              <Typography color="secondary">Save Changes</Typography>
            </Button>
            <Button
              size="small"
              className="mg_left_8"
              onClick={handleCancel}
              disabled={saving}
              data-testid="cancel-auth-methods"
              startIcon={<HighlightOffIcon className="theme_color_orange" />}
            >
              <Typography className="theme_color_orange">Cancel</Typography>
            </Button>
          </>
        )
      }
    >
      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress size={24} />
        </Grid>
      ) : (
        <Grid container spacing={2} alignItems="center">
          {AUTH_METHOD_OPTIONS.map((option) => (
            <Grid
              key={option.value}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              className="d-flex align-center"
            >
              <Typography variant="body1" color="textSecondary">
                {option.label}
              </Typography>
              <ToggleTickSwitch
                className="mg_left_8"
                checked={methods.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                disabled={saving}
                name={`auth-method-${option.value}`}
                inputProps={{
                  'aria-label': `Toggle ${option.label} authentication`,
                  'data-testid': `auth-method-${option.value}`,
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </SectionWrapper>
    </div>
  );
};

AuthMethodsSection.propTypes = {
  level: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
};

export default AuthMethodsSection;
